'use client';

import { Button, Dropdown, Typography } from 'components';
import { CLIENTS_SING_IN_LINK, TRANSLATORS_SIGN_IN_LINK } from 'constants/links';
import { menu } from 'constants/menu';
import { LogoGreenClipArt } from 'lib/ClipArts';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { FC, PropsWithChildren, useMemo } from 'react';
import { cn } from 'utils/cn';
import HamburguerIcon from './HamburguerIcon';

interface INavbar {
  small?: boolean;
}

const Navbar: FC<PropsWithChildren<INavbar>> = ({ small }) => {
  const t = useTranslations();
  const pathname = usePathname();

  const isTranslators = pathname.includes('translators');

  const signs = useMemo(
    () => [
      { key: 'sign_in', href: isTranslators ? TRANSLATORS_SIGN_IN_LINK : CLIENTS_SING_IN_LINK },
      { key: 'sign_up', href: '/sign-up' }
    ],
    [isTranslators]
  );

  const activePath = menu.findIndex((path) => pathname.includes(path));

  return (
    <header
      className={cn(
        'sticky top-0 z-50 h-full bg-[hsla(0,0%,100%,.85)] backdrop-blur-xl transition-colors duration-200 ease-in-out',
        small && 'shadow-box-navbar'
      )}
      style={{
        transition: 'padding-bottom .16s cubic-bezier(.61,1,.88,1)'
      }}
    >
      <div className="inline-flex size-full flex-col items-center justify-start">
        <div className="block size-full max-w-6xl px-6 py-[20px] sm:px-16 lg:py-[14px]">
          <div
            className={cn('flex h-full items-center justify-between', small ? 'p-0' : 'lg:py-6')}
            style={{ transition: 'padding .16s cubic-bezier(.61,1,.88,1)' }}
          >
            <div className="flex">
              <Link className="flex h-full items-center font-medium" href="/">
                <div className="relative w-[150px]">
                  <LogoGreenClipArt className="w-[60px] sm:w-[100px]" />
                </div>
              </Link>
              <div className="hidden items-center gap-10 lg:flex ">
                {menu.map((path, index) => (
                  <Typography
                    key={path}
                    variant="medium3"
                    className={cn(
                      'select-none text-tolq-black-100 hover:text-tolq-black-200',
                      activePath === index && 'text-tolq-blue-100'
                    )}
                  >
                    <Link href={`/${path}`}>{t(`navbar.${path}`)}</Link>
                  </Typography>
                ))}
              </div>
            </div>
            <div className="flex h-8 flex-col items-center justify-center lg:hidden">
              <Dropdown
                variant="hamburger"
                items={[
                  {
                    name: t(`navbar.home`),
                    href: '/'
                  },
                  ...menu.map((path) => ({
                    name: t(`navbar.${path}`),
                    href: `/${path}`
                  })),
                  ...signs.map(({ key, href }) => ({
                    name: t(`navbar.${key}`),
                    href
                  }))
                ]}
              >
                <HamburguerIcon />
              </Dropdown>
            </div>
            <div className="hidden items-center gap-4 lg:flex">
              <Link href={signs[0].href}>
                <Button size="medium" variant="text" label={t('navbar.sign_in')} />
              </Link>
              <Link href={signs[1].href}>
                <Button
                  size="medium"
                  variant="outlined"
                  color="secondary"
                  label={t('navbar.sign_up')}
                  className="!py-[21px]"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
